import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BrregCompanyNameField } from "@/components/new-project/BrregCompanyNameField";
import { cn } from "@/lib/utils";
import { useFreelancerDisciplines, useFreelancers } from "@/hooks/use-freelancers";
import { useSaveFreelancer, type FreelancerInput } from "@/hooks/use-freelancer-mutations";
import type { BrregHit } from "@/hooks/use-brreg-search";
import type { FreelancerKind } from "@/lib/db-types";
import { findDuplicates, parseKroner, parseTags, type DuplicateReason, type FreelancerListRow } from "@/lib/freelancers";
import { DisciplinePicker } from "./DisciplinePicker";
import { FreelancerLinksEditor } from "./FreelancerLinksEditor";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Satt = rediger. */
  freelancer?: FreelancerListRow | null;
  onSaved?: (id: string) => void;
}

const REASON_TEXT: Record<DuplicateReason, string> = {
  email: "samme e-post",
  phone: "samme telefonnummer",
  organization_number: "samme org.nr",
  name: "samme navn",
};

function emptyInput(): FreelancerInput {
  return {
    name: "", kind: "person", company_name: null, organization_number: null, email: null, phone: null,
    location: null, day_rate_nok: null, hour_rate_nok: null, rate_note: null, links: [], tags: [], notes: null, discipline_ids: [],
  };
}

function fromRow(row: FreelancerListRow): FreelancerInput {
  return {
    name: row.name, kind: row.kind, company_name: row.company_name, organization_number: row.organization_number,
    email: row.email, phone: row.phone, location: row.location, day_rate_nok: row.day_rate_nok, hour_rate_nok: row.hour_rate_nok,
    rate_note: row.rate_note, links: row.links, tags: row.tags, notes: row.notes, discipline_ids: row.disciplines.map((d) => d.id),
  };
}

export function FreelancerFormDialog({ open, onOpenChange, freelancer, onSaved }: Props) {
  // Startverdier fra props; kallerne gir dialogen ny key ved hver åpning.
  const [input, setInput] = useState<FreelancerInput>(() => (freelancer ? fromRow(freelancer) : emptyInput()));
  const [tagText, setTagText] = useState(() => freelancer?.tags.join(", ") ?? "");
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  const { data: disciplines = [] } = useFreelancerDisciplines();
  const { data: all = [] } = useFreelancers();
  const save = useSaveFreelancer();

  const set = (patch: Partial<FreelancerInput>) => {
    setInput((prev) => ({ ...prev, ...patch }));
    setConfirmDuplicate(false);
  };

  // Ved redigering varsles det bare når navn, e-post, telefon eller org.nr endres,
  // ellers måtte en bekreftet dublett bekreftes på nytt ved hver lagring.
  const identityChanged =
    !freelancer ||
    (["name", "email", "phone", "organization_number"] as const).some((k) => (input[k] ?? "").trim() !== (freelancer[k] ?? "").trim());
  const duplicates = useMemo(
    () =>
      identityChanged && (input.name.trim() || input.email || input.phone || input.organization_number)
        ? findDuplicates(input, all, freelancer?.id)
        : [],
    [identityChanged, input, all, freelancer?.id],
  );

  const applyHit = (hit: BrregHit) =>
    input.kind === "company"
      ? set({ name: hit.navn, organization_number: hit.organisasjonsnummer, location: input.location ?? hit.poststed })
      : set({ company_name: hit.navn, organization_number: hit.organisasjonsnummer });

  const canSave = input.name.trim().length > 0 && input.discipline_ids.length > 0 && (duplicates.length === 0 || confirmDuplicate);

  const submit = () => {
    save.mutate(
      { id: freelancer?.id, input: { ...input, tags: parseTags(tagText) } },
      { onSuccess: (row) => { onOpenChange(false); onSaved?.(row.id); } },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{freelancer ? "Rediger frilanser" : "Ny frilanser"}</DialogTitle>
          <DialogDescription>Navn og ett fagfelt er nok. Resten kan fylles inn senere.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2" role="radiogroup" aria-label="Person eller firma">
            {(["person", "company"] as FreelancerKind[]).map((k) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={input.kind === k}
                onClick={() => set({ kind: k })}
                className={cn(
                  "h-7 rounded-pill border-[1.5px] px-3 text-xs font-medium",
                  input.kind === k ? "border-foreground bg-foreground text-background" : "border-border bg-card",
                )}
              >
                {k === "person" ? "Person" : "Firma"}
              </button>
            ))}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="freelancer-name">{input.kind === "company" ? "Firmanavn *" : "Navn *"}</Label>
            {input.kind === "company" ? (
              <BrregCompanyNameField name={input.name} onNameChange={(name) => set({ name })} onHitSelect={applyHit} />
            ) : (
              <Input id="freelancer-name" value={input.name} onChange={(e) => set({ name: e.target.value })} autoComplete="off" />
            )}
          </div>

          {input.kind === "person" && (
            <div className="space-y-1.5">
              <Label>Selskap</Label>
              <BrregCompanyNameField name={input.company_name ?? ""} onNameChange={(company_name) => set({ company_name })} onHitSelect={applyHit} />
              <p className="text-xs text-muted-foreground">Treff fra Brønnøysundregistrene fyller ut org.nr. Kan stå tomt.</p>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="freelancer-org">Org.nr</Label>
              <Input id="freelancer-org" value={input.organization_number ?? ""} onChange={(e) => set({ organization_number: e.target.value })} inputMode="numeric" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="freelancer-phone">Telefon</Label>
              <Input id="freelancer-phone" value={input.phone ?? ""} onChange={(e) => set({ phone: e.target.value })} type="tel" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="freelancer-location">Sted</Label>
              <Input id="freelancer-location" value={input.location ?? ""} onChange={(e) => set({ location: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="freelancer-email">E-post</Label>
            <Input id="freelancer-email" value={input.email ?? ""} onChange={(e) => set({ email: e.target.value })} type="email" />
          </div>

          <div className="space-y-1.5">
            <Label>Fagfelt *</Label>
            <DisciplinePicker disciplines={disciplines} value={input.discipline_ids} onChange={(discipline_ids) => set({ discipline_ids })} />
            <p className="text-xs text-muted-foreground">Det første (★) er hovedfagfeltet. Dobbeltklikk et valgt fagfelt for å gjøre det til hovedfagfelt.</p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="freelancer-day-rate">Dagspris (8 t)</Label>
              <Input id="freelancer-day-rate" inputMode="numeric" value={input.day_rate_nok?.toLocaleString("nb-NO") ?? ""} onChange={(e) => set({ day_rate_nok: parseKroner(e.target.value) })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="freelancer-hour-rate">Timepris</Label>
              <Input id="freelancer-hour-rate" inputMode="numeric" value={input.hour_rate_nok?.toLocaleString("nb-NO") ?? ""} onChange={(e) => set({ hour_rate_nok: parseKroner(e.target.value) })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="freelancer-rate-note">Prisnotat</Label>
            <Input id="freelancer-rate-note" value={input.rate_note ?? ""} onChange={(e) => set({ rate_note: e.target.value })} placeholder="Halvdag 60 %, inkl. kamera, eks. mva" />
            <p className="text-xs text-muted-foreground">Veiledende. Brukes ikke i beregninger.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="freelancer-tags">Kjennetegn</Label>
            <Input id="freelancer-tags" value={tagText} onChange={(e) => setTagText(e.target.value)} placeholder="Periode, Vestlandsdialekt, Har ikke førerkort" />
            <p className="text-xs text-muted-foreground">Skill med komma.</p>
          </div>

          <div className="space-y-1.5">
            <Label>Lenker</Label>
            <FreelancerLinksEditor value={input.links} onChange={(links) => set({ links })} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="freelancer-notes">Internt notat</Label>
            <Textarea id="freelancer-notes" value={input.notes ?? ""} onChange={(e) => set({ notes: e.target.value })} rows={3} />
            <p className="text-xs text-muted-foreground">Ikke helseopplysninger eller andre sensitive forhold. Frilanseren kan be om innsyn.</p>
          </div>

          {duplicates.length > 0 && (
            <div role="alert" className="space-y-2 rounded-md border-[1.5px] border-amber-600 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-medium">Finnes kanskje allerede</p>
              <ul className="space-y-1">
                {duplicates.slice(0, 3).map(({ row, reason }) => (
                  <li key={row.id}>
                    <Link to={`/frilansere/${row.id}`} className="font-medium underline" onClick={() => onOpenChange(false)}>{row.name}</Link>
                    {" "}har {REASON_TEXT[reason]}{row.archived_at ? " (arkivert)" : ""}.
                  </li>
                ))}
              </ul>
              {!confirmDuplicate && (
                <Button type="button" variant="outline" size="sm" onClick={() => setConfirmDuplicate(true)}>
                  {freelancer ? "Lagre likevel" : "Opprett likevel"}
                </Button>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button type="button" onClick={submit} disabled={!canSave || save.isPending}>
            {freelancer ? "Lagre" : "Opprett"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
